import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { z } from 'zod'

import {
  hasPendingSubscription,
  isSubscriber,
  type SubscriptionFields,
  trialDaysLeft,
} from '@langmail/auth-config/access'
import { useAuth } from '@langmail/shared-ui'

import { Screen } from '@/components/screen'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AccountsList } from '@/features/account/components/accounts-list'
import { AppPasswords } from '@/features/account/components/app-passwords'
import { AppleProfile } from '@/features/account/components/apple-profile'
import { LangmailAddress } from '@/features/account/components/langmail-address'
import { useDeleteAccount } from '@/features/account/hooks/use-delete-account'

const CONFIRMATION_TEXT: string = 'DELETE'

const formSchema = z.object({
  confirmText: z.string().refine((val) => val === CONFIRMATION_TEXT, {
    message: `You must type "${CONFIRMATION_TEXT}" to confirm`,
  }),
})

// One line of status for the Plan row; the subscribe page has the detail.
function planLabel(user: SubscriptionFields | null): string {
  if (!user) {
    return ''
  }
  if (hasPendingSubscription(user)) {
    return 'Payment pending'
  }
  const daysLeft = trialDaysLeft(user)
  if (daysLeft !== null) {
    return `Trial, ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
  }
  switch (user.subscription_status) {
    case 'active':
      return isSubscriber(user) ? 'Subscribed' : 'Early access'
    case 'past_due':
      return 'Payment past due'
    default:
      return 'No active plan'
  }
}

export function Accounts() {
  const { user } = useAuth()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const deleteAccountMutation = useDeleteAccount()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      confirmText: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (values.confirmText !== CONFIRMATION_TEXT) {
      return
    }

    await deleteAccountMutation.mutateAsync({})
    setShowDeleteDialog(false)
    form.reset()
  }

  return (
    <Screen title="Accounts">
      <div>
        {/* Langmail Address Section */}
        <LangmailAddress />

        {/* Mail-client app passwords */}
        <AppPasswords />

        {/* iOS setup profile */}
        <AppleProfile />

        {/* Connected Accounts Section */}
        <div className="mb-8">
          <AccountsList />
        </div>

        {/* Plan */}
        <div className="mb-8">
          <p className="mb-1 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
            Plan
          </p>
          <div className="flex items-center justify-between">
            <p className="text-[15px]">{planLabel(user)}</p>
            <Link className="text-primary text-sm hover:underline" to="/subscribe">
              Manage plan
            </Link>
          </div>
        </div>

        {/* Divider */}
        <hr className="my-8 border-border border-t" />

        {/* Delete Data Button */}
        <AlertDialog onOpenChange={setShowDeleteDialog} open={showDeleteDialog}>
          <AlertDialogTrigger asChild>
            <button className="text-destructive text-sm hover:underline" type="button">
              Delete my data
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your account and remove all your data from
                our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <Form {...form}>
              <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name="confirmText"
                  render={({ field }) => (
                    <FormItem>
                      <FormDescription>
                        Type <span className="font-semibold">{CONFIRMATION_TEXT}</span> to confirm
                      </FormDescription>
                      <FormControl>
                        <Input placeholder={CONFIRMATION_TEXT} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleteAccountMutation.isPending}
                    onClick={() => {
                      setShowDeleteDialog(false)
                      form.reset()
                    }}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleteAccountMutation.isPending}
                    type="submit"
                  >
                    {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </form>
            </Form>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Screen>
  )
}
